import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight } from "lucide-react";
import { HeroCanvas } from "./HeroCanvas";

const EASE = [0.22, 1, 0.36, 1];

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1 md:pb-2">
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.05, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [0, 200]);
  const opacity = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-screen flex-col overflow-hidden"
    >
      <HeroCanvas />
      <motion.div
        style={{ y, opacity }}
        className="relative z-10 mx-auto flex w-full max-w-7xl flex-1 flex-col justify-center px-6 pb-24 pt-36 md:pt-44"
      >
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15, ease: EASE }}
          className="mb-8 inline-flex w-fit items-center gap-2.5 rounded-full border border-kinetic-border bg-kinetic-light px-4 py-2"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-kinetic animate-pulse-dot" />
          <span className="font-mono text-[11px] font-medium uppercase tracking-[0.2em] text-kinetic-hover">
            Covelli — Marketing Agency · Est. 2026
          </span>
        </motion.div>

        <h1
          data-testid="hero-title-masked"
          className="font-display text-6xl font-extrabold uppercase leading-[0.9] tracking-tighter text-slate-900 sm:text-7xl md:text-8xl lg:text-[7.5rem]"
        >
          <MaskedLine delay={0.35}>Get Ready</MaskedLine>
          <MaskedLine delay={0.5}>
            <span className="text-kinetic">To Scale</span>
          </MaskedLine>
        </h1>

        <span className="mt-8 block overflow-hidden">
          <motion.span
            data-testid="hero-coming-soon"
            className="flex items-center gap-4"
            initial={{ y: "130%" }}
            animate={{ y: "0%" }}
            transition={{ duration: 0.9, delay: 0.72, ease: EASE }}
          >
            <span className="h-px w-12 bg-kinetic" aria-hidden="true" />
            <span className="font-mono text-xs uppercase tracking-[0.45em] text-slate-500 md:text-sm">
              Now booking new clients
            </span>
          </motion.span>
        </span>

        <motion.p
          data-testid="hero-subtitle"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.95, ease: EASE }}
          className="mt-8 max-w-3xl text-lg font-normal leading-relaxed text-slate-600 md:text-xl"
        >
          <span className="block font-bold text-slate-900 md:whitespace-nowrap">
            Covelli is a marketing agency built for brands that refuse to
            blend in.
          </span>
          We pair sharp strategy with standout creative to turn attention
          into measurable, lasting growth. From brand identity to full-funnel
          marketing, we work alongside ambitious teams who want to move fast,
          think clearly, and win with confidence.
          <span className="block font-bold text-kinetic">This is Covelli.</span>
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.15, ease: EASE }}
          className="mt-12 flex flex-wrap items-center gap-4"
        >
          <Link
            to="/contact"
            data-testid="hero-book-call-button"
            className="group inline-flex items-center gap-2 rounded-full bg-kinetic px-8 py-4 font-display text-sm font-bold uppercase tracking-wide text-white transition-colors duration-300 hover:bg-kinetic-hover"
          >
            Book a Call
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          <Link
            to="/pricing"
            data-testid="hero-pricing-link"
            className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-8 py-4 font-display text-sm font-bold uppercase tracking-wide text-slate-700 transition-colors duration-300 hover:border-skybrand hover:text-skybrand"
          >
            See Packages & Pricing
          </Link>
        </motion.div>
      </motion.div>

      <motion.a
        href="#manifesto"
        data-testid="hero-scroll-hint"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 0.8 }}
        className="absolute bottom-8 left-6 z-10 flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.25em] text-slate-400 transition-colors duration-300 hover:text-kinetic md:left-1/2 md:-translate-x-1/2"
      >
        <ArrowDown size={14} className="animate-float-slow" />
        Scroll the manifesto
      </motion.a>
    </section>
  );
};
